import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/internships.scss";
import { useI18n } from "../../i18n/I18nProvider";

// ==================== HOOKS ====================

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

// ==================== DATA ====================

const INTRO_TEXT =
    "Centrum Cyberbezpieczeństwa zaprasza studentów do odbycia praktyk studenckich, które umożliwiają zdobycie doświadczenia w obszarze cyberbezpieczeństwa, nowych technologii oraz funkcjonowania nowoczesnych organizacji. Oferujemy możliwość pracy przy projektach edukacyjnych, badawczych i popularyzujących wiedzę o bezpieczeństwie cyfrowym.";

const DIRECTIONS = [
    "Informatyki",
    "Marketingu i nowych mediów",
    "Zarządzania",
];

const SKILLS_TEXT =
    "Praktyki w Centrum Cyberbezpieczeństwa pozwalają rozwijać kompetencje praktyczne, pracować w interdyscyplinarnym zespole oraz realizować zadania mające realny wpływ na rozwój projektów związanych z bezpieczeństwem w cyberprzestrzeni.";

const APPLICATION_REQUIREMENTS = [
    "kierunek studiów",
    "obszar zainteresowań (informatyka, marketing, zarządzanie)",
    "preferowany termin odbycia praktyk",
];

const CONTACT_EMAIL = "cc@wsiz.edu.pl";

// ==================== COMPONENT ====================

const InternshipsPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const directions = [t("internships_direction_1"), t("internships_direction_2"), t("internships_direction_3")];
    const applicationReqs = [t("internships_req_1"), t("internships_req_2"), t("internships_req_3")];
    const { ref: refIntro, inView: inViewIntro } = useInView(0.15);
    const { ref: refDirections, inView: inViewDirections } = useInView(0.15);
    const { ref: refSkills, inView: inViewSkills } = useInView(0.15);
    const { ref: refApply, inView: inViewApply } = useInView(0.15);
    const { ref: refCta, inView: inViewCta } = useInView(0.15);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("intern-page", { "intern-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("internships_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

           
            <MatrixHero
                title={t("internships_hero_title")}
                subtitle={t("internships_hero_subtitle")}
                density={0.5}
            />

            <div className="intern-page__inner">

               
                <article
                    ref={refIntro}
                    className={classNames("intern-card", { "intern-card--visible": inViewIntro })}
                >
                    <div className="intern-card__glow" aria-hidden="true" />
                    <h2 className="intern-card__title">{t("internships_intro_title")}</h2>
                    <p className="intern-card__text">
                        <TypewriterText text={t("internships_intro_text")} active={inViewIntro} speed={4} />
                    </p>
                </article>

               
                <div
                    ref={refDirections}
                    className={classNames("intern-directions", { "intern-directions--visible": inViewDirections })}
                >
                    <h2 className="intern-directions__title">{t("internships_directions_title")}</h2>
                    <div className="intern-directions__grid">
                        {directions.map((dir, i) => (
                            <div
                                key={i}
                                className="direction-tile"
                                style={{ animationDelay: `${i * 0.12}s` }}
                            >
                              
                                <span className="direction-tile__text">{dir}</span>
                            </div>
                        ))}
                    </div>
                </div>

               
                <article
                    ref={refSkills}
                    className={classNames("intern-card intern-card--accent", { "intern-card--visible": inViewSkills })}
                >
                    <div className="intern-card__glow" aria-hidden="true" />
                    <h2 className="intern-card__title">{t("internships_skills_title")}</h2>
                    <p className="intern-card__text">
                        <TypewriterText text={t("internships_skills_text")} active={inViewSkills} speed={4} />
                    </p>
                </article>

               
                <article
                    ref={refApply}
                    className={classNames("intern-card", { "intern-card--visible": inViewApply })}
                >
                    <div className="intern-card__glow" aria-hidden="true" />
                    <h2 className="intern-card__title">{t("internships_apply_title")}</h2>
                    <p className="intern-card__text">
                        {t("internships_apply_text")}
                    </p>
                    <p className="intern-card__text">{t("internships_apply_lead")}</p>
                    <ul className="intern-requirements">
                        {applicationReqs.map((req, i) => (
                            <li key={i} className="intern-requirements__item">{req}</li>
                        ))}
                    </ul>
                </article>

               
                <div
                    ref={refCta}
                    className={classNames("intern-cta", { "intern-cta--visible": inViewCta })}
                >
                    <h2 className="intern-cta__heading">{t("internships_cta_heading")}</h2>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="intern-cta__email">
                        {CONTACT_EMAIL}
                    </a>
                </div>

            </div>
        </section>
    );
};

export default InternshipsPage;
